import classnames from 'classnames';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { trackMixpanelPageView } from 'helpers/post';
import {
  postEventAccountFieldValidate,
  registrationFieldUpdate,
  registrationPostEvent,
  setRole,
} from 'redux/ducks/Registration';
import CustomCheckBox from 'components/V2/CustomInputs/CustomCheckBox';
import { Link } from 'react-router-dom';
import Google from 'components/OAuth/Google';
import Microsoft from 'components/OAuth/Microsoft';
import { requireOrganizationData } from 'helpers/dashboard';
import { mapQuerySearchParams, QUERY_SEARCH_STRING, validateQuerySearchString } from '@helpers/localStorage';
import { ORGANIZATION_TYPES, ORGANIZATION_TYPES_REGISTRATION } from '@helpers/enums';
import { useQueryParamCapture } from '@helpers/useQueryParamCapture';
import { Button, UncontrolledAlert } from 'reactstrap';
import './SignUp.scss';
import { SegmentedStepper } from '@dev-go4ellis/go4-design-system';
import { getFunnelStepperConfig } from '../funnelSteps';

const validateFieldsNames = ['firstName', 'lastName', 'email', 'password', 'passwordConfirmation', 'terms'];
const SIGN_UP_ERROR_MESSAGES = {
  domain_blocked: (
    <>
      Currently your account can not be created, please{' '}
      <a href="mailto:contactus@go4.io">contact Go4 support</a>.
    </>
  ),
};

const SignUp = ({
  history,
  updateField,
  user,
  validateField,
  validation,
  setRole,
  registrationPostEvent,
  ...props
}) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const orgType = localStorage.getItem('registrationOrgType');
  const funnelStepper = getFunnelStepperConfig('/create-account/sign-up');

  // Automatically capture query parameters (whitelisted route)
  useQueryParamCapture();

  useEffect(() => {
    setRole('EventOperator');
    updateField('postEventMasterType', '');
    updateField('terms', true);
    // orgType is now handled automatically by useQueryParamCapture hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRole, updateField]);

  useEffect(() => {
    trackMixpanelPageView(`/create-account/sign-up/orgType=${orgType}`, user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const searchData = JSON.parse(localStorage.getItem(QUERY_SEARCH_STRING));
    if (!searchData || searchData?.searchParamsSet) return;

    const params = new URLSearchParams(searchData.searchParams);
    params.forEach((value, key) => {
      const newKey = mapQuerySearchParams[key];

      if (validateQuerySearchString(newKey, value)) {
        const newValue =
          newKey === 'defaultEventSettingId'
            ? ORGANIZATION_TYPES.find(({ name }) => name === value)?.id
            : value;
        updateField(newKey, newValue);
      }
    });

    localStorage.setItem(
      QUERY_SEARCH_STRING,
      JSON.stringify({
        ...searchData,
        searchParamsSet: true,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const orgName = localStorage.getItem('registrationOrganizationName');
    const orgWebsite = localStorage.getItem('registrationOrganizationWebsite');
    const zipCode = localStorage.getItem('registrationZipCode');

    if (orgName) updateField('companyName', orgName);
    if (orgWebsite) updateField('organizationWebsite', orgWebsite);
    if (zipCode) updateField('addressZip', zipCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFieldValidate = field => {
    validateField(field);
  };

  const onCreateAccount = () => {
    window.grecaptcha.ready(function () {
      console.log('grecaptcha ready');
      window.grecaptcha
        .execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY_V3, { action: 'signup' })
        .then(function (token) {
          updateField('token', token);
          registrationPostEvent(history, '/create-account/ready-to-post');
        });
    });
  };

  const onNext = e => {
    e.preventDefault();
    validateFieldsNames.forEach(field => {
      onFieldValidate(field);
    });

    // Check if all required fields are filled
    const hasErrors =
      validation.errors.firstName ||
      validation.errors.lastName ||
      validation.errors.email ||
      validation.errors.password ||
      validation.errors.passwordConfirmation;

    if (
      !hasErrors &&
      user.firstName &&
      user.lastName &&
      user.email &&
      user.password &&
      user.passwordConfirmation &&
      user.terms
    ) {
      updateField('defaultEventSettingId', ORGANIZATION_TYPES_REGISTRATION[orgType]);
      onCreateAccount();
    }
  };

  const onFieldChange = (field, triggerValidate) => {
    return ev => {
      updateField(field, ev.currentTarget.value);
      if (triggerValidate) {
        onFieldValidate(field);
      }
    };
  };

  const onTermsChanged = e => {
    updateField('terms', e.target.checked);
    validateField('terms');
  };

  const renderErrorAlert = () => {
    const { error, registrationError } = props;

    if (
      registrationError &&
      registrationError.response &&
      registrationError.response.data &&
      typeof registrationError.response.data === 'string'
    ) {
      const errorMessage = registrationError.response.data;
      const errorKey = Object.keys(SIGN_UP_ERROR_MESSAGES).find(key => errorMessage.includes(key));
      const message = SIGN_UP_ERROR_MESSAGES[errorKey] ?? errorMessage;

      return (
        <UncontrolledAlert color="danger" className="alert-margin">
          {message}
        </UncontrolledAlert>
      );
    }

    if (
      error &&
      error.response &&
      error.response.data?.error &&
      typeof error.response.data.error === 'string'
    ) {
      const errorMessage = error.response.data.error;
      return (
        <UncontrolledAlert color="danger" className="alert-margin">
          {errorMessage}
        </UncontrolledAlert>
      );
    }

    if (error && error.message) {
      return (
        <UncontrolledAlert color="danger" className="alert-margin">
          {error.message}
        </UncontrolledAlert>
      );
    }

    return null;
  };

  const hyperlinkText = () => {
    return (
      <React.Fragment>
        By creating an account I have read and agree to the{'\n'}{' '}
        <a target="_blank" rel="noopener noreferrer" href="https://go4.io/privacy-policy/">
          Privacy Policy
        </a>
        {', '}{' '}
        <a target="_blank" rel="noopener noreferrer" href="https://go4.io/terms-and-conditions/">
          Terms and Conditions
        </a>{' '}
        {', '}{' '}
        <a href=" https://stripe.com/legal/connect-account" target="_blank" rel="noopener noreferrer">
          Stripe Connected Account Agreement
        </a>{' '}
        and{' '}
        <a href=" https://www.go4.io/go4-marketplace-standards/  " target="_blank" rel="noopener noreferrer">
          Go4 Cancellation Policy.
        </a>
      </React.Fragment>
    );
  };

  const trackLogin = () => {
    trackMixpanelPageView('IntroLogInClick');
  };
  return (
    <div className="v3SignUpScene gotham-regular-font">
      <main>
        {funnelStepper && (
          <div className="next-steps-mobile-stepper">
            <SegmentedStepper total={funnelStepper.total} current={funnelStepper.current} size="sm" />
          </div>
        )}
        <header>
          <div className="title gotham-bold-font">Create your account</div>
        </header>
        <div className="sign-up-notice">5,000+ organizations trust Go4 to protect their sidelines.</div>
        {renderErrorAlert()}

        <div className="oauth-container mt-4 mb-4">
          {process.env.REACT_APP_MICROSOFT_OAUTH_ENABLED === 'true' ? (
            <div className="row">
              <div className="col-lg-6 gotham-medium-font mb-2 mb-lg-0">
                <Google step="event-intro" />
              </div>
              <div className="col-lg-6 gotham-medium-font mt-2 mt-lg-0">
                <Microsoft step="event-intro" />
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-lg-12 gotham-medium-font mb-2 mb-lg-0">
                <Google step="event-intro" />
              </div>
            </div>
          )}
        </div>

        <div className="or-divider">
          <div className="divider-line"></div>
          <span>OR</span>
          <div className="divider-line"></div>
        </div>

        <form
          id="accountForm"
          className="v3signupForm nimbus-regular-font"
          noValidate
          onSubmit={e => e.preventDefault()}
        >
          <div className="row">
            <div className="col-lg-6">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <div className="input-group">
                  <input
                    type="text"
                    className={classnames('form-control', {
                      'is-invalid': validation.errors.firstName,
                    })}
                    name="firstName"
                    id="firstName"
                    value={user.firstName}
                    onChange={onFieldChange('firstName')}
                    onBlur={onFieldValidate.bind(this, 'firstName')}
                    autoFocus
                  />
                </div>
                {validation.errors.firstName && (
                  <div className="invalid-feedback">{validation.errors.firstName}</div>
                )}
              </div>
            </div>

            <div className="col-lg-6">
              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <div className="input-group">
                  <input
                    type="text"
                    className={classnames('form-control', {
                      'is-invalid': validation.errors.lastName,
                    })}
                    name="lastName"
                    id="lastName"
                    value={user.lastName}
                    onChange={onFieldChange('lastName')}
                    onBlur={onFieldValidate.bind(this, 'lastName')}
                  />
                </div>
                {validation.errors.lastName && (
                  <div className="invalid-feedback">{validation.errors.lastName}</div>
                )}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-group">
              <input
                type="email"
                className={classnames('form-control', {
                  'is-invalid': validation.errors.email,
                })}
                name="email"
                id="email"
                value={user.email}
                onChange={onFieldChange('email')}
                onBlur={onFieldValidate.bind(this, 'email')}
              />
            </div>
            {validation.errors.email && <div className="invalid-feedback">{validation.errors.email}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-group">
              <input
                type={showPassword ? 'text' : 'password'}
                className={classnames('form-control password-input', {
                  'is-invalid': validation.errors.password,
                })}
                value={user.password}
                name="password"
                id="password"
                placeholder={'Password must be 8+ characters'}
                onChange={e => {
                  onFieldChange('password')(e);
                  updateField('passwordConfirmation', e.currentTarget.value);
                }}
                onBlur={onFieldValidate.bind(this, 'password')}
              />
              <i
                className={`show-password fa ${showPassword ? 'fa-eye' : 'fa-eye-slash'}`}
                aria-hidden="true"
                onClick={() => setShowPassword(!showPassword)}
              ></i>
            </div>
            {validation.errors.password && (
              <div className="invalid-feedback">{validation.errors.password}</div>
            )}
          </div>

          <CustomCheckBox
            id="terms"
            name="terms"
            checked={!!user.terms}
            onClick={onTermsChanged}
            className="terms-checkbox pl-0 hide-checkbox"
            center
          >
            {hyperlinkText()}
          </CustomCheckBox>
        </form>
      </main>

      <div className="navigation-buttons">
        <div className="nav-login-container">
          <div className="nav-login-text">Already have an account?</div>
          <Link to="/" onClick={trackLogin} className="login-btn gotham-medium-font">
            <div>Log In</div>
          </Link>
        </div>

        <Button
          onClick={onNext}
          className={classnames('gotham-bold-font signup')}
          disabled={
            !user.firstName ||
            !user.lastName ||
            !user.email ||
            !user.password ||
            !user.passwordConfirmation ||
            !user.terms
          }
        >
          <div>Create Account</div>
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { authenticated, currentUser } = state.session;
  const organizationRequired = requireOrganizationData(currentUser, authenticated);

  if (authenticated && currentUser && organizationRequired) {
    ownProps.history.push('/v2/create-event/organization');
  }

  if (authenticated && currentUser && !organizationRequired) {
    ownProps.history.push('/dashboard');
  }

  return {
    user: state.registration.user,
    validation: state.registration.postEventAccountValidation,
    error: state.session.error,
    registrationError: state.registration.error,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateField: (field, value) => dispatch(registrationFieldUpdate(field, value)),
    validateField: field => dispatch(postEventAccountFieldValidate(field)),
    setRole: role => dispatch(setRole(role)),
    registrationPostEvent: (history, location) =>
      dispatch(registrationPostEvent({ history, location, step: 'event-intro' })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SignUp);
